import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { apiKeyManager } from './api-key-manager';
import { getDeliveryAdminApiBaseUrl } from '../lib/api-base-url';

export interface DeliveryAuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (apiKey: string) => Promise<boolean>;
  logout: () => void;
}

const DeliveryAuthContext = createContext<DeliveryAuthContextType | undefined>(undefined);

export const DeliveryAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Check for existing API key on mount
  useEffect(() => {
    const hasKey = apiKeyManager.hasApiKey();
    setIsAuthenticated(hasKey);
    setIsInitialized(true);
  }, []);

  const login = async (apiKey: string): Promise<boolean> => {
    // Validate the API key using the dedicated auth validation endpoint
    const apiBaseUrl = getDeliveryAdminApiBaseUrl();

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/auth/validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          apiKeyManager.setApiKey(apiKey);
          setIsAuthenticated(true);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('API key validation failed:', error);
      return false;
    }
  };

  const logout = () => {
    apiKeyManager.clearApiKey();
    setIsAuthenticated(false);
  };

  return (
    <DeliveryAuthContext.Provider
      value={{
        isAuthenticated,
        isInitialized,
        login,
        logout,
      }}
    >
      {children}
    </DeliveryAuthContext.Provider>
  );
};

export function useDeliveryAuth(): DeliveryAuthContextType {
  const context = useContext(DeliveryAuthContext);
  if (!context) {
    throw new Error('useDeliveryAuth must be used within a DeliveryAuthProvider');
  }
  return context;
}
